import { DropdownOptionModel } from '../../../Forms/models/dropdown-option.model';
import { BehaviorSubject, Observable } from 'rxjs';


export enum FallbackInputTypeEnum {
    text = 'text',
    dateTime = 'dateTime',
    date = 'date',
    time = 'time',
    duration = 'duration',
    dropdown = 'dropdown',
    number = 'number',
    autocomplete = 'autocomplete',
    timezone = 'timezone'
}

export class CsvFieldDefinition<T> {
    fieldId: number;
    label: string;
    guessingHelperWords: string[];
    addToModel?: (model: T, value: any, fallback?: any) => void;
    validator?: (value: any) => boolean;
    transform?: (value: string) => string;

    // For displaying of red asterix, no validation is done using this at the time
    required?: boolean;

    dropdownLabel?: string;
    fallbackInputType?: FallbackInputTypeEnum;
    defaultFallbackValue?: any;
    fallbackDropdownOptions?: DropdownOptionModel[];
    fallbackValueValidator?: (value: any) => boolean;
    isFallbackValueValid?: boolean;
    fallbackValueErrorMessage?: string;
    max?: number;
    step?: number;
    integerOnly?: boolean;
    maxLength?: number;
    placeholder?: string;
    fallbackSuggestionsFetcher?: (query: string, locale?: string) => Observable<string[]>;
    dateValidationFormatDropdownOptions?: DropdownOptionModel[];
    timeValidationFormatDropdownOptions?: DropdownOptionModel[];
}
export class CsvColumn {
    columnIndex: number;
    columnLabel: string;
    exactMatch?: boolean;
}

export class CsvField<T> {
    originalField: CsvFieldDefinition<T>;
    fileIndex: number;
    targetColumn: CsvColumn;
    requiredError: boolean = false;
    exampleValue: string;
    fallbackValue: any;
    fallbackValueError: boolean = false;
    selectedDateValidationFormat: DropdownOptionModel;
    dateValidationFormatRequiredError: boolean = false;
    selectedTimeValidationFormat: DropdownOptionModel;
    timeValidationFormatRequiredError: boolean = false;
}

export class ImportedFile<T, Y> {
    hasHeaderRow: boolean;

    fileName: string;
    rawData: string[][];

    fileParameters: Y;

    // Each row of the displayed grid
    csvFields: CsvField<T>[] = [];

    // What the user can select in any of the dropdowns
    // The first element is used to "clear" the input
    // TODO : Localize this label
    csvColumns: CsvColumn[] = [{
        columnIndex: null,
        columnLabel: 'Discover.csvImporter.ignoreField',
        exactMatch: false
    }];
}

export abstract class CsvImportDialogService<T, Y> {
    protected fieldDefinitions: CsvFieldDefinition<T>[];

    protected importedFiles: ImportedFile<T, Y>[] = [];
    private importedFilesSubject: BehaviorSubject<ImportedFile<T, Y>[]> = new BehaviorSubject<ImportedFile<T, Y>[]>(this.importedFiles);
    public importedFiles$ = this.importedFilesSubject.asObservable();

    protected abstract findFieldMappingsFromDataRow(file: ImportedFile<T, Y>);
    protected abstract findHeaderRow(file: ImportedFile<T, Y>);
    protected abstract createEmptyOutputModel(file: ImportedFile<T, Y>): T;

    // Returns a list of error localization strings
    public abstract getValidationErrorsInOneFile(file: ImportedFile<T, Y>): string[];
    public abstract importData(importParameters?: any): Observable<any>;
    public abstract setFieldDefinitions(importType?: any);

    public abstract fileParameterFactory(): Y;

    public initialize() {
        this.importedFiles = [];
    }

    public processData(fileName: string, data: string[][]) {
        if (this.importedFiles.find(x => x.fileName === fileName || data.length === 0)) {
            return;
        }

        const file = new ImportedFile<T, Y>();
        file.fileParameters = this.fileParameterFactory();
        const fileIndex = this.importedFiles.findIndex(x => x.fileName === fileName);

        this.buildCsvFields(file, fileIndex);

        file.fileName = fileName;
        data = data.filter(x => !(x.length === 1 && x[0] === '\n'));
        file.rawData = data;

        this.findHeaderRow(file);

        if (file.hasHeaderRow) {
            this.findFieldMappingsFromHeaderRow(file);
        } else {
            this.findFieldMappingsFromDataRow(file);
        }

        this.updateExampleValues(file);
        this.importedFiles.push(file);
        this.importedFilesSubject.next(this.importedFiles);
    }

    public updateExampleValue(file: ImportedFile<T, Y>, field: CsvField<T>) {
        if (field.targetColumn) {
            // This is the dummy "field clearer" value which has no id
            if (field.targetColumn.columnIndex === null) {
                field.targetColumn = null;
                field.exampleValue = null;
            } else {
                // have made sure file.rawData is not empty
                const firstDataRowIndex = Math.min(file.hasHeaderRow ? 1 : 0, file.rawData.length - 1);
                for (let i = firstDataRowIndex; i < file.rawData.length; i++) {
                    // find a non empty one..
                    field.exampleValue = file.rawData[i][field.targetColumn.columnIndex]?.trim();
                    if (field.exampleValue) {
                        break;
                    }
                }
            }
        } else {
            field.exampleValue = null;
        }
    }

    protected constructOutputModels(): T[] {

        const outputModels: T[] = [];
        for (const importedFile of this.importedFiles) {
            const start = importedFile.hasHeaderRow ? 1 : 0;

            for (let i = start; i < importedFile.rawData.length; i++) {
                const model: T = this.createEmptyOutputModel(importedFile);
                outputModels.push(model);

                importedFile.csvFields.forEach(csvField => {
                    if (csvField.targetColumn) {
                        csvField.originalField.addToModel(model, importedFile.rawData[i][csvField.targetColumn.columnIndex]);
                    }
                });
            }
        }
        return outputModels;
    }

    protected findFieldMappingsFromHeaderRow(file: ImportedFile<T, Y>) {

        for (let i = 0; i < file.rawData[0].length; i++) {
            const csvColumn = {
                columnIndex: i,
                columnLabel: file.rawData[0][i],
                exactMatch: false,
            };

            file.csvColumns.push(csvColumn);

            let matchedFields = [];
            for (const value of this.fieldDefinitions) {
                // Try exact match first and fall back on includes otherwise
                let foundWord = value.guessingHelperWords.find(helperWord => file.rawData[0][i].toLowerCase() === helperWord.toLowerCase());
                if (!foundWord) {
                    foundWord = value.guessingHelperWords.find(helperWord => file.rawData[0][i].toLowerCase().includes(helperWord.toLowerCase()));
                } else {
                    csvColumn.exactMatch = true;
                }
                if (foundWord) {
                    if (csvColumn.exactMatch) {
                        matchedFields = [value];
                        break;
                    } else {
                        matchedFields.push(value);
                    }
                    value.dropdownLabel = file.rawData[0][i];
                }
            }

            if (matchedFields.length >= 1) {
                const csvField = file.csvFields.find(x => x.originalField === matchedFields[0]);
                if (!csvField.targetColumn || (!csvField.targetColumn.exactMatch && csvColumn.exactMatch)) {
                    csvField.targetColumn = csvColumn;
                }
            }
        }
    }

    protected buildCsvFields(file: ImportedFile<T, Y>, fileIndex: number): void {
        this.fieldDefinitions.forEach((value: CsvFieldDefinition<T>, fieldId: number) => {
            const csvField: CsvField<T> = new CsvField();
            csvField.originalField = value;
            if (value.defaultFallbackValue !== null || value.defaultFallbackValue !== undefined) {
                csvField.fallbackValue = value.defaultFallbackValue;
            }
            csvField.fileIndex = fileIndex;
            file.csvFields.push(csvField);
        });
    }

    protected updateExampleValues(file: ImportedFile<T, Y>) {
        file.csvFields.forEach(x => this.updateExampleValue(file, x));
    }

    protected setTargetColumn(file: ImportedFile<T, Y>, fieldId: number, columnIndex: number) {
        const csvField = file.csvFields.find(x => x.originalField.fieldId === fieldId);
        if (csvField) {
            csvField.targetColumn = file.csvColumns.find(x => x.columnIndex === columnIndex);
        }
    }

    public getValidationErrors(): string[] {
        const errors = new Set<string>();
        this.importedFiles
            .forEach(file =>
                this.getValidationErrorsInOneFile(file).forEach(error => errors.add(error)));
        return Array.from(errors);
    }

    public clearImportedFiles(): void {
        this.importedFiles = [];
        this.importedFilesSubject.next(this.importedFiles);
    }
}
